import { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query';

import { formatDateForApiWithoutTimezone } from 'core/utils/date-serialization';

export type RelativeDateFilterType =
  | 'today'
  | 'yesterday'
  | 'this-week'
  | 'last-week'
  | 'this-month'
  | 'last-month'
  | 'this-quarter'
  | 'last-quarter'
  | 'this-year'
  | 'last-year'
  | 'last-7-days'
  | 'last-30-days'
  | 'last-90-days'
  | 'last-365-days'
  | 'custom';

export type RelativeDateFilterOption = {
  type: RelativeDateFilterType;
  label: string;
  description: string;
  category: 'relative' | 'periods' | 'ranges';
};

export type RelativeDateFilterField =
  | 'dateCreated'
  | 'studyDate'
  | 'uploadedDate'
  | 'assignedDate'
  | 'completedDate'
  | 'returnedDate'
  | 'requestedDate'
  | 'customerUploadedDate'
  | 'dateModified';

/**
 * Serializable relative date filter configuration that can be stored in worklist dataState
 */
export type RelativeDateFilterConfig = {
  type: 'relative-date-filter';
  field: RelativeDateFilterField;
  filterType: RelativeDateFilterType;
  version: 1; // For future compatibility
  fromDate?: string; // ISO string for custom ranges
  toDate?: string; // ISO string for custom ranges
};

export const RELATIVE_DATE_FILTER_OPTIONS: RelativeDateFilterOption[] = [
  // Relative options
  { type: 'today', label: 'Today', description: 'Items from today onwards', category: 'relative' },
  { type: 'yesterday', label: 'Yesterday', description: 'Items from yesterday only', category: 'relative' },

  // Period options
  { type: 'this-week', label: 'This Week', description: 'Items from this week onwards', category: 'periods' },
  { type: 'last-week', label: 'Last Week', description: 'Items from last week only', category: 'periods' },
  { type: 'this-month', label: 'This Month', description: 'Items from this month onwards', category: 'periods' },
  { type: 'last-month', label: 'Last Month', description: 'Items from last month only', category: 'periods' },
  { type: 'this-quarter', label: 'This Quarter', description: 'Items from this quarter onwards', category: 'periods' },
  { type: 'last-quarter', label: 'Last Quarter', description: 'Items from last quarter only', category: 'periods' },
  { type: 'this-year', label: 'This Year', description: 'Items from this year onwards', category: 'periods' },
  { type: 'last-year', label: 'Last Year', description: 'Items from last year only', category: 'periods' },

  // Range options
  { type: 'last-7-days', label: 'Last 7 Days', description: 'Items from the last 7 days', category: 'ranges' },
  { type: 'last-30-days', label: 'Last 30 Days', description: 'Items from the last 30 days', category: 'ranges' },
  { type: 'last-90-days', label: 'Last 90 Days', description: 'Items from the last 90 days', category: 'ranges' },
  { type: 'last-365-days', label: 'Last 365 Days', description: 'Items from the last 365 days', category: 'ranges' },
  { type: 'custom', label: 'Custom Range', description: 'Items within a custom date range', category: 'ranges' },
];

export const DATE_FIELD_OPTIONS: { field: RelativeDateFilterField; label: string; description: string }[] = [
  { field: 'dateCreated', label: 'Date Created', description: 'When the exam was created in the system' },
  { field: 'uploadedDate', label: 'Uploaded Date', description: 'When the exam was uploaded to the system' },
  { field: 'studyDate', label: 'Study Date', description: 'When the exam was performed' },
  { field: 'assignedDate', label: 'Assigned Date', description: 'When the exam was assigned to a physician' },
  { field: 'completedDate', label: 'Completed Date', description: 'When the exam was completed' },
  { field: 'returnedDate', label: 'Returned Date', description: 'When the exam was returned' },
  { field: 'requestedDate', label: 'Requested Date', description: 'When the exam was requested' },
  { field: 'customerUploadedDate', label: 'Customer Upload Date', description: 'When the customer uploaded the exam' },
  { field: 'dateModified', label: 'Modified Date', description: 'When the exam was last modified' },
];

/**
 * Gets the date range for a relative date filter type
 */
export function getRelativeDateRange(
  filterType: RelativeDateFilterType,
  fromDate?: Date | null,
  toDate?: Date | null,
): { startDate: Date; endDate: Date | null } {
  // Handle custom date range
  if (filterType === 'custom') {
    if (!fromDate) {
      throw new Error('Custom date range requires fromDate');
    }
    return { startDate: fromDate, endDate: toDate || null };
  }
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

  switch (filterType) {
    case 'today':
      return { startDate: startOfDay, endDate: endOfDay };

    case 'yesterday': {
      const yesterday = new Date(startOfDay);
      yesterday.setDate(yesterday.getDate() - 1);
      const endYesterday = new Date(yesterday);
      endYesterday.setHours(23, 59, 59, 999);
      return { startDate: yesterday, endDate: endYesterday };
    }

    case 'this-week': {
      const startOfWeek = new Date(startOfDay);
      const dayOfWeek = startOfWeek.getDay();
      startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek); // Sunday
      return { startDate: startOfWeek, endDate: null };
    }

    case 'last-week': {
      const startOfLastWeek = new Date(startOfDay);
      const dayOfWeek = startOfLastWeek.getDay();
      startOfLastWeek.setDate(startOfLastWeek.getDate() - dayOfWeek - 7); // Last Sunday
      const endOfLastWeek = new Date(startOfLastWeek);
      endOfLastWeek.setDate(endOfLastWeek.getDate() + 6);
      endOfLastWeek.setHours(23, 59, 59, 999);
      return { startDate: startOfLastWeek, endDate: endOfLastWeek };
    }

    case 'this-month': {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      return { startDate: startOfMonth, endDate: null };
    }

    case 'last-month': {
      const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      return { startDate: startOfLastMonth, endDate: endOfLastMonth };
    }

    case 'this-quarter': {
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const startOfQuarter = new Date(now.getFullYear(), currentQuarter * 3, 1);
      return { startDate: startOfQuarter, endDate: null };
    }

    case 'last-quarter': {
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
      const year = currentQuarter === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const startOfLastQuarter = new Date(year, lastQuarter * 3, 1);
      const endOfLastQuarter = new Date(year, lastQuarter * 3 + 3, 0, 23, 59, 59, 999);
      return { startDate: startOfLastQuarter, endDate: endOfLastQuarter };
    }

    case 'this-year': {
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      return { startDate: startOfYear, endDate: null };
    }

    case 'last-year': {
      const startOfLastYear = new Date(now.getFullYear() - 1, 0, 1);
      const endOfLastYear = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
      return { startDate: startOfLastYear, endDate: endOfLastYear };
    }

    case 'last-7-days': {
      const sevenDaysAgo = new Date(startOfDay);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return { startDate: sevenDaysAgo, endDate: null };
    }

    case 'last-30-days': {
      const thirtyDaysAgo = new Date(startOfDay);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return { startDate: thirtyDaysAgo, endDate: null };
    }

    case 'last-90-days': {
      const ninetyDaysAgo = new Date(startOfDay);
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      return { startDate: ninetyDaysAgo, endDate: null };
    }

    case 'last-365-days': {
      const oneYearAgo = new Date(startOfDay);
      oneYearAgo.setDate(oneYearAgo.getDate() - 365);
      return { startDate: oneYearAgo, endDate: null };
    }

    default:
      return { startDate: startOfDay, endDate: null };
  }
}

/**
 * Creates a Kendo filter descriptor for a relative date filter
 */
export function createRelativeDateFilter(
  field: RelativeDateFilterField,
  filterType: RelativeDateFilterType,
  fromDate?: Date | null,
  toDate?: Date | null,
): CompositeFilterDescriptor {
  const { startDate, endDate } = getRelativeDateRange(filterType, fromDate, toDate);

  // For "current" filters (today, this week, this month, this quarter, this year),
  // use only the start date to make them open-ended and prevent staleness
  const isCurrentPeriodFilter = ['today', 'this-week', 'this-month', 'this-quarter', 'this-year'].includes(filterType);

  if (isCurrentPeriodFilter) {
    // Open-ended filter - only start date constraint
    return {
      logic: 'and',
      filters: [
        {
          field,
          operator: 'gte',
          value: startDate,
        } as FilterDescriptor,
      ],
    };
  } else if (endDate) {
    // Closed range filter - both start and end date constraints
    return {
      logic: 'and',
      filters: [
        {
          field,
          operator: 'gte',
          value: startDate,
        } as FilterDescriptor,
        {
          field,
          operator: 'lte',
          value: endDate,
        } as FilterDescriptor,
      ],
    };
  } else {
    return {
      logic: 'and',
      filters: [
        {
          field,
          operator: 'gte',
          value: startDate,
        } as FilterDescriptor,
      ],
    };
  }
}

/**
 * Creates a serializable relative date filter configuration
 */
export function createRelativeDateFilterConfig(
  field: RelativeDateFilterField,
  filterType: RelativeDateFilterType,
  fromDate?: Date | null,
  toDate?: Date | null,
): RelativeDateFilterConfig {
  return {
    type: 'relative-date-filter',
    field,
    filterType,
    version: 1,
    fromDate: fromDate ? formatDateForApiWithoutTimezone(fromDate) : undefined,
    toDate: toDate ? formatDateForApiWithoutTimezone(toDate) : undefined,
  };
}

/**
 * Converts a relative date filter config to a Kendo filter descriptor
 */
export function relativeDateConfigToFilter(config: RelativeDateFilterConfig): CompositeFilterDescriptor {
  const fromDate = config.fromDate ? new Date(config.fromDate) : null;
  const toDate = config.toDate ? new Date(config.toDate) : null;
  return createRelativeDateFilter(config.field, config.filterType, fromDate, toDate);
}

/**
 * Gets a human-readable description of the active relative date filter
 */
export function getRelativeDateFilterDescription(field: RelativeDateFilterField, filterType: RelativeDateFilterType): string {
  const fieldOption = DATE_FIELD_OPTIONS.find((f) => f.field === field);
  const filterOption = RELATIVE_DATE_FILTER_OPTIONS.find((f) => f.type === filterType);

  if (!fieldOption || !filterOption) {
    return 'Unknown filter';
  }

  return `${fieldOption.label}: ${filterOption.label}`;
}

/**
 * Checks if a filter is a relative date filter created by this utility
 */
export function isRelativeDateFilter(filter: FilterDescriptor | CompositeFilterDescriptor): boolean {
  return (
    'filters' in filter &&
    filter.logic === 'and' &&
    (filter.filters?.length === 1 || filter.filters?.length === 2) && // Allow 1 or 2 filters
    filter.filters.every((f) => 'field' in f && DATE_FIELD_OPTIONS.some((option) => option.field === f.field) && (f.operator === 'gte' || f.operator === 'lte'))
  );
}

/**
 * Checks if an object is a relative date filter configuration
 */
export function isRelativeDateFilterConfig(obj: unknown): obj is RelativeDateFilterConfig {
  return (
    obj != null &&
    typeof obj === 'object' &&
    (obj as Record<string, unknown>).type === 'relative-date-filter' &&
    typeof (obj as Record<string, unknown>).field === 'string' &&
    typeof (obj as Record<string, unknown>).filterType === 'string' &&
    DATE_FIELD_OPTIONS.some((option) => option.field === (obj as Record<string, unknown>).field) &&
    RELATIVE_DATE_FILTER_OPTIONS.some((option) => option.type === (obj as Record<string, unknown>).filterType)
  );
}

/**
 * Extracts the relative date filter information from a Kendo filter
 */
export function parseRelativeDateFilter(filter: CompositeFilterDescriptor): {
  field: RelativeDateFilterField;
  startDate: Date;
  endDate?: Date; // endDate is now optional for open-ended filters
} | null {
  if (!isRelativeDateFilter(filter)) {
    return null;
  }

  const gteFilter = filter.filters?.find((f) => 'operator' in f && f.operator === 'gte') as FilterDescriptor;
  const lteFilter = filter.filters?.find((f) => 'operator' in f && f.operator === 'lte') as FilterDescriptor;

  if (!gteFilter) {
    return null;
  }

  // For open-ended filters, there might not be an lteFilter
  if (lteFilter && gteFilter.field !== lteFilter.field) {
    return null;
  }

  return {
    field: gteFilter.field as RelativeDateFilterField,
    startDate: new Date(gteFilter.value),
    endDate: lteFilter ? new Date(lteFilter.value) : undefined,
  };
}

/**
 * Finds and extracts relative date filter configs from a dataState JSON string
 */
export function extractRelativeDateFiltersFromDataState(dataStateJson: string | null): RelativeDateFilterConfig[] {
  if (!dataStateJson) return [];

  try {
    const dataState = JSON.parse(dataStateJson);
    const configs: RelativeDateFilterConfig[] = [];

    // Look for relative date filter configs in the dataState
    const findConfigs = (obj: unknown): void => {
      if (Array.isArray(obj)) {
        obj.forEach(findConfigs);
      } else if (obj && typeof obj === 'object') {
        if (isRelativeDateFilterConfig(obj)) {
          configs.push(obj);
        } else {
          Object.values(obj).forEach(findConfigs);
        }
      }
    };

    findConfigs(dataState);
    return configs;
  } catch {
    return [];
  }
}

/**
 * Backend API parameters for relative date filters
 */
export type RelativeDateFilterApiParams = {
  relativeDateField?: RelativeDateFilterField;
  relativeDateType?: RelativeDateFilterType;
};

/**
 * Converts relative date filter config to backend API parameters
 */
export function relativeDateConfigToApiParams(config: RelativeDateFilterConfig | null): RelativeDateFilterApiParams {
  if (!config) return {};

  return {
    relativeDateField: config.field,
    relativeDateType: config.filterType,
  };
}
